import { z } from 'zod';

/**
 * The base runtime contract for one enrichment session, shared by both
 * enrichment features that describe a session:
 *  - `sessions` (the admin table: GET /enrichment-sessions rows), and
 *  - `session-detail` (GET /enrichment-sessions/:id, which returns exactly these
 *    fields plus per-target results / admin actions / progress; see
 *    `session-detail`'s `sessionDetailSchema`, which extends this).
 *
 * It lives in `_shared` (owned by neither feature) so the detail can extend the
 * list's contract without a cross-feature import. Counters default to 0 and
 * timestamps tolerate null/absent so a partial row degrades gracefully instead of
 * throwing at the boundary. Pure; no React/Redux/fetch.
 *
 * TODO(palier-2): promote to @shared/schemas so backend and frontend share one
 * definition (same follow-up as enrichmentStatsSchema).
 */
/**
 * One step of the connector chain a session runs, as the list emits it.
 *
 * Three fields, because three are what the table's Chain column reads: it draws
 * one pip per step coloured by `status`, names it by its `connector`, and orders
 * them by `position`. The per-step job counters are the detail page's business;
 * the list already carries the target-scoped totals aggregated across the whole
 * run (`enriched_count` / `skipped_count` / `error_count`).
 *
 * `status` stays a plain string, like the session's own: a `StepStatus` the
 * frontend has never heard of should render as unknown, not throw.
 */
export const sessionStepSchema = z.object({
  connector: z.string(),
  position: z.number().default(0),
  status: z.string(),
});

export type SessionStep = z.infer<typeof sessionStepSchema>;

/**
 * The connectors a run chains, as display labels.
 *
 * The backend has already turned the connector codes into labels and joined them
 * with an arrow (`mapPipelineAsSession`), so the chain is read back out of that
 * one string rather than re-derived.
 *
 * The hyphen stripping stays although the labels no longer carry one. A label is
 * computed on read, never stored, so nothing in the database holds the old
 * "LinkedIn - Profiles" spelling; but a browser tab opened before the deploy,
 * or a response still in a cache, does, and it should render as "LinkedIn
 * Profiles" rather than as a code being spelled out. Three characters of regex
 * against a class of rows nobody can enumerate.
 *
 * Shared: the list names its steps from it (the Chain column) and so does the
 * detail (its pipeline cards), and neither feature may reach into the other.
 */
export function connectorChain(type: string): string[] {
  return type
    .split('→')
    .map((part) => part.trim().replace(/\s+-\s+/g, ' '))
    .filter((part) => part.length > 0);
}

/** Which kind of target a connector addresses. */
export type ConnectorEntity = 'profile' | 'company';

/**
 * Profiles or companies, read off the label the backend already resolved.
 *
 * This is what lets a mark stand beside the word; the icon on the table's
 * chips, the one the New Session panel puts on the same two choices. The word
 * stays, because a mark alone is unreadable to anyone who has not learnt it.
 *
 * The test is on "companies", not on the whole label, and that is what survived
 * dropping the "LinkedIn" prefix: "Companies" matches exactly as "LinkedIn -
 * Companies" did, so a label from either side of the rename reads the same.
 *
 * Anything that is not a company connector is a profile connector, which mirrors
 * `isCompanyConnector` on the server, the authority on this, since it decides
 * which table a step's target ids resolve against. A connector nobody has heard
 * of gets the profile mark rather than none, for the same reason.
 *
 * Reads the label rather than the connector code because that is all a session
 * row carries by the time it gets here: `type` is already a resolved,
 * arrow-joined string, and legacy rows have no `steps` at all.
 */
export function connectorEntity(label: string): ConnectorEntity {
  return /companies/i.test(label) ? 'company' : 'profile';
}

/**
 * What a whole run is pointed at, read off its label.
 *
 * The chain's *first* connector, not the whole string: a two-step run's label
 * holds both, and asking `connectorEntity` about it would answer "company" for
 * any run that ends on one. A `Profiles → Companies` session is pointed at
 * profiles; the companies are what its second step derives from them, and
 * `total_profiles` counts the profiles either way.
 */
export function sessionEntity(type: string): ConnectorEntity {
  return connectorEntity(connectorChain(type)[0] ?? '');
}

/**
 * The word for the things a run is pointed at: "profile", "companies".
 *
 * "Target" is how the code names them, and the code is right to: a target is
 * whatever a connector is handed, and the two tables it can resolve against are
 * exactly what makes one word necessary there. On screen it is jargon an admin
 * has to translate, and the translation is always the same one (profiles, or
 * companies) which the session already knows. So the screens say it (#530).
 *
 * "Results" was the word the request suggested, and it is the one word this
 * cannot take: the session detail already spends it on *step* results, one row
 * per target × step, deliberately a different number from the target count.
 * Two counts under one word on the same screen is the confusion the rename was
 * meant to remove.
 */
export function entityNoun(count: number, entity: ConnectorEntity): string {
  if (entity === 'company') return count === 1 ? 'company' : 'companies';
  return count === 1 ? 'profile' : 'profiles';
}

/** "1 profile", "3 companies", "2 failed profiles": the subject a sentence takes. */
export function entitySubject(count: number, entity: ConnectorEntity, qualifier = ''): string {
  return `${count} ${qualifier ? `${qualifier} ` : ''}${entityNoun(count, entity)}`;
}

// `statusLabel` moved to `@/shared/ui/status-label` when the client Imports
// page (#708) took the same pill; re-exported so its readers here are unchanged.
export { statusLabel } from '@/shared/ui/status-label';

/**
 * What a run was asked for, as opposed to who it resolved to.
 *
 * Deliberately loose. The set of filters will move, and a session recorded under
 * an older shape still has to render: unknown keys pass through rather than
 * being dropped, every known one is optional, and anything that is not an object
 * at all degrades to `{}` instead of failing the row it belongs to; one
 * malformed recipe must not empty the whole table.
 *
 * The licence, the list and `force` are NOT here: they have columns of their
 * own, and repeating them would create two places to disagree.
 */
export const sessionCriteriaSchema = z
  .looseObject({
    /** Profile-type filter values, `UNSET_TYPE_VALUE` included. */
    types: z.array(z.string()).optional(),
    /** Which buckets ran: `new`, `stale`, `fresh`. */
    buckets: z.array(z.string()).optional(),
    /** Cap on the whole cohort; 0 or absent means none. */
    cap: z.number().optional(),
    /** Company runs only: restricted to where the scoped profiles work today. */
    currentOnly: z.boolean().optional(),
    /**
     * Who asked, when it was not a person: `MONITORED_AUTO_ENRICHMENT_SOURCE`
     * for the daily re-enrichment of tracked profiles (#519). Absent on every
     * run an admin launched by hand.
     */
    source: z.string().optional(),
  })
  .catch({});

export type SessionCriteria = z.infer<typeof sessionCriteriaSchema>;

export const enrichmentSessionSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.string(),
  /**
   * The connector chain's steps. Empty for a legacy session (it predates
   * pipelines and has none) and defaulted so the detail endpoint, which
   * extends this contract without emitting steps, still parses.
   */
  steps: z.array(sessionStepSchema).default([]),
  // The licence/list a batch was run against, with their display names resolved by the
  // backend. All four are nullable: a company batch never carries a list, a pipeline's
  // licence is optional, and either id can be orphaned (no FK backs them, and lists are
  // hard-deleted), in which case the name comes back null and the UI shows a dash.
  licence_id: z.string().nullable().default(null),
  licence_name: z.string().nullable().default(null),
  list_id: z.string().nullable().default(null),
  list_name: z.string().nullable().default(null),
  admin_name: z.string().default(''),
  total_profiles: z.number().default(0),
  /** Defaults to `{}` for every run created before the column existed. */
  criteria: sessionCriteriaSchema.default({}),
  enriched_count: z.number().default(0),
  skipped_count: z.number().default(0),
  error_count: z.number().default(0),
  started_at: z.string().nullable().default(null),
  completed_at: z.string().nullable().default(null),
  created_at: z.string(),
});

export type EnrichmentSession = z.infer<typeof enrichmentSessionSchema>;
